// src/components/landing/FAQ.tsx
'use client'

import { useState, useCallback } from 'react'
import { ChevronDown } from 'lucide-react'
import { FREE_TEST_ALLOWANCES, PRO_TEST_ALLOWANCES } from '@/lib/billing/plan-copy'

export type FAQItem = {
  q: string
  a: string
}

export const FAQ_ITEMS: FAQItem[] = [
  {
    // Built from plan-copy (i.e. PLANS), not typed out. This used to say Pro
    // "unlocks ... the Checkpoint diagnostic" while Pricing — and the start
    // route — gave Free one Checkpoint a month.
    q: 'Is UnlimitedTests free?',
    a: `The Free plan includes the diagnostic, daily drills, unlimited topic practice, and ${FREE_TEST_ALLOWANCES}. Pro gives you ${PRO_TEST_ALLOWANCES}, plus adaptive practice and more daily AI-tutor questions.`,
  },
  {
    q: 'How does the adaptive testing work?',
    a: 'Your Module 2 difficulty adjusts based on M1 performance, mirroring the real Digital SAT.',
  },
  {
    q: 'Can I cancel anytime?',
    a: "Yes — cancel your PRO subscription anytime from the billing portal. Cancellation takes effect at the end of your current billing period: you keep full PRO access until then, and we don't refund partial months.",
  },
  {
    q: 'Which tests are available right now?',
    a: 'The SAT is fully live with a complete question bank. PSAT, ACT, and AP exams are coming next — on the vote page you can help us decide which AP subjects to build first. Your subscription will unlock every test as it ships, at no extra cost.',
  },
  {
    // No dates. This used to promise "PSAT in Q3 2026, ACT in Q4 2026, AP
    // through 2027" — placeholder estimates that went stale on a public page,
    // read by the schools we pilot with. Each test ships when its question
    // bank has been reviewed, and we say that instead of a quarter.
    q: 'When will PSAT, ACT, and AP go live?',
    a: "We're not putting dates on them yet: each one goes live when its question bank has been built and reviewed, not before. In the meantime, the PSAT uses the same format as the Digital SAT, so SAT practice here prepares you for it too. Vote for the AP subjects you want first, and we'll email you the moment each one ships.",
  },
  {
    q: 'Is there a mobile app?',
    a: 'The site is a Progressive Web App — install to your home screen for an app-like experience.',
  },
  {
    // Go-live audit: no back-tested error distribution exists, so no numeric
    // accuracy claim ("±40") — describe the inputs honestly instead.
    q: 'How accurate are the score predictions?',
    a: 'Predictions blend your real practice-test performance with official College Board scaling curves, and they get more reliable as you complete more full-length tests.',
  },
]

/**
 * Pure toggle helper used by FAQ items. Exported for unit testing.
 * - If `index` equals `openIndex`, returns `null` (close).
 * - Otherwise, returns `index` (open that item — accordion style, only one open at a time).
 */
export function toggleFaq(openIndex: number | null, index: number): number | null {
  return openIndex === index ? null : index
}

type Props = {
  items?: FAQItem[]
  /** Optional initial open index (for SSR / tests) */
  initialOpen?: number | null
}

export default function FAQ({ items = FAQ_ITEMS, initialOpen = null }: Props) {
  const [openIndex, setOpenIndex] = useState<number | null>(initialOpen)

  const handleToggle = useCallback((index: number) => {
    setOpenIndex((prev) => toggleFaq(prev, index))
  }, [])

  return (
    <section id="faq" className="scroll-mt-20 px-5 py-14 md:px-10">
      <div className="mx-auto max-w-3xl">
        <h2 className="mb-3 text-center text-2xl font-semibold text-foreground md:text-3xl">
          Frequently asked questions
        </h2>
        <p className="mb-10 text-center text-sm text-muted-foreground">
          Everything you need to know before you start.
        </p>

        <ul className="space-y-3" data-testid="faq-list">
          {items.map((item, i) => {
            const isOpen = openIndex === i
            const panelId = `faq-panel-${i}`
            const btnId = `faq-button-${i}`
            return (
              <li
                key={item.q}
                className="overflow-hidden rounded-xl border border-border bg-card/60 transition hover:border-border"
                data-testid="faq-item"
              >
                <button
                  id={btnId}
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => handleToggle(i)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-foreground hover:bg-card/80 focus:outline-none focus:ring-2 focus:ring-brand/40"
                  data-testid="faq-button"
                >
                  <span className="text-sm font-medium md:text-base">{item.q}</span>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                    aria-hidden
                  />
                </button>
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={btnId}
                  hidden={!isOpen}
                  className="border-t border-border px-5 py-4 text-sm text-muted-foreground"
                  data-testid="faq-panel"
                >
                  {item.a}
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    </section>
  )
}
